import { useEffect, useRef, useState } from "react";
import { api } from "@/helper/api";
import { store } from "@/components/state/store";
import { KAER_COMPANY_SUBJECT_ID } from "@/helper/systemIds";

export interface KaerUserOption {
  value: string;
  label: string;
}

export type ActiveKaerUsersVariant = "plain" | "withRelations";


let cache: Map<string, Promise<KaerUserOption[]>> = new Map();

export function resetActiveKaerUsersCache() {
  cache = new Map();
}

export function fetchActiveKaerUsers(
  variant: ActiveKaerUsersVariant = "plain"
): Promise<KaerUserOption[]> {
  const parentSubjectId = store.getState().parent?.subjectId ?? "";
  const cacheKey = variant + ":" + parentSubjectId;
  const cached = cache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const additionalUrlParameters: Record<string, string | boolean> = {
    impersonate_subject: KAER_COMPANY_SUBJECT_ID,
    is_active: "true",
  };
  if (variant === "withRelations") {
    additionalUrlParameters["include_relations"] = true;
  }

  const request = new Promise<KaerUserOption[]>((resolve, reject) => {
    api.genericApiRequest({
      entity: "user",
      method: "get",
      parametersToRender: {
        additionalUrlParameters: additionalUrlParameters,
        depth: "0",
      },
      /*eslint-disable */
      successHandler: (res: any) => {
        const options = res.data.results.map((user: any) => ({
          value:
            variant === "withRelations" ? user.person.subject : user.subject_id,
          label: user.last_name + ", " + user.first_name,
        }));
        resolve(
          options.sort((a: KaerUserOption, b: KaerUserOption) =>
            a.label.localeCompare(b.label)
          )
        );
      },
      failHandler: (error: any) => {
        reject(error);
      },
      /*eslint-enable */
    });
  }).catch((error) => {
    // do not cache failures, so the next consumer retries
    if (cache.get(cacheKey) === request) {
      cache.delete(cacheKey);
    }
    throw error;
  });

  cache.set(cacheKey, request);
  return request;
}


export function useActiveKaerUsers(opts?: {
  variant?: ActiveKaerUsersVariant;
  onError?: (error: unknown) => void;
}) {
  const variant = opts?.variant ?? "plain";
  const [userOptions, setUserOptions] = useState<KaerUserOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown | null>(null);

  const onErrorRef = useRef(opts?.onError);
  onErrorRef.current = opts?.onError;

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    fetchActiveKaerUsers(variant)
      .then((options) => {
        if (!active) return;
        setUserOptions(options);
        setError(null);
      })
      .catch((err) => {
        if (!active) return;
        setError(err);
        onErrorRef.current?.(err);
      })
      .finally(() => {
        if (!active) return;
        setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [variant]);

  return { userOptions, isLoading, error };
}
